/**
 * Resolve platform-facing game-end outcomes from bgio `ctx.gameover`.
 *
 * Games vary: `victory.winner` / `winner` may be a logical seat id ("white")
 * or a bgio seat index string ("0"). Prefer an id that matches `playerIds`,
 * then fall back to index → id.
 */

export type GameEndOutcome =
  | { kind: 'win'; playerId: string }
  | { kind: 'draw' }
  | { kind: 'over' }

type GameoverRecord = {
  draw?: boolean
  winner?: string
  victory?: { winner?: string }
}

/** Map a raw winner token onto a known logical player id, if possible. */
function resolveWinnerPlayerId(
  record: GameoverRecord,
  playerIds: readonly string[],
): string | undefined {
  const candidates = [record.victory?.winner, record.winner]
  for (const candidate of candidates) {
    if (typeof candidate !== 'string' || !candidate) continue
    if (playerIds.includes(candidate)) return candidate
    const slot = Number.parseInt(candidate, 10)
    if (Number.isInteger(slot) && slot >= 0 && slot < playerIds.length) {
      return playerIds[slot]
    }
  }
  return undefined
}

/**
 * Returns null when the game is still in progress.
 * Otherwise: win (with logical player id), draw, or a generic over.
 */
export function resolveGameEndOutcome(
  gameover: unknown,
  playerIds: readonly string[],
): GameEndOutcome | null {
  if (!gameover || typeof gameover !== 'object') return null

  const record = gameover as GameoverRecord
  const winnerId = resolveWinnerPlayerId(record, playerIds)
  if (winnerId) return { kind: 'win', playerId: winnerId }
  if (record.draw === true) return { kind: 'draw' }
  return { kind: 'over' }
}
